"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useForm } from "react-hook-form"
import ThemedSkeleton from "../../ui/ThemedSkeleton"
import { NaicsCodeSelector } from "../business_profile/NaicsCodeSelector"

import { FaLongArrowAltRight, FaLongArrowAltLeft } from "react-icons/fa"
import { useGetBusinessProfile } from "../business_profile/useGetBusinessProfile"
import { useUpdateBusinessProfile } from "../business_profile/useUpdateBusinessProfile"
import toast from "react-hot-toast"

export function ProfileForm({ onStepComplete, onStepPrevious }) {
  const [pending, setPending] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [naicsCodes, setNaicsCodes] = useState([])

  const { data: profile, isLoading: isLoadingProfile, error: fetchError } = useGetBusinessProfile()

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      website: "",
      profile_description: "",
    },
  })

  const { mutateAsync: updateBusinessProfile, isLoading: isUpdatingProfile } = useUpdateBusinessProfile()

  useEffect(() => {
    if (profile) {
      setValue("website", profile.website || "")
      setValue("profile_description", profile.profile_description || "")
      setNaicsCodes(profile.naics_codes || [])
    }
  }, [profile, setValue])

  async function onSubmit(formData) {
    setPending(true)

    try {
      const parsedData = {
        ...formData,
        naics_codes: naicsCodes,
      }

      const result = await updateBusinessProfile(parsedData)

      if (result) {
        onStepComplete()
      } else {
        throw new Error("Profile update failed. Please try again later.")
      }
    } catch (error) {
      console.error("Error updating profile:", error)
      toast.error(error.message || "An unexpected error occurred. Please try again.")
    } finally {
      setPending(false)
    }
  }

  async function handleGenerateProfile() {
    setIsGenerating(true)
    try {
      const website = getValues("website")

      if (!website) {
        throw new Error("Please enter a website before generating the profile.")
      }

      // Call updateBusinessProfile with only the website
      const result = await updateBusinessProfile({ website })

      if (result) {
        // Update NAICS codes and profile description from the result
        setNaicsCodes(result.naics_codes || [])
        setValue("profile_description", result.profile_description || "")
        toast.success("NAICS codes and company profile generated successfully!")
      } else {
        throw new Error("Failed to generate profile. Please try again.")
      }
    } catch (error) {
      console.error("Error generating profile:", error)
      toast.error(error.message || "Failed to generate profile. Please try again.")
    } finally {
      setIsGenerating(false)
    }
  }

  if (fetchError) {
    return (
      <div className="text-red-500">
        <p>Unable to load your profile information. Please refresh the page or try again later.</p>
      </div>
    )
  }

  if (isLoadingProfile) {
    return (
      <div className="flex flex-col gap-5">
        <ThemedSkeleton className="h-10 w-5/6" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10 w-5/6" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10 w-5/6" />
      </div>
    )
  }

  return (
    <Card className="w-full max-w-4xl bg-white text-black shadow-md rounded-lg">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Edit Your Business Profile</h1>
            <div className="bg-blue-50 p-4 rounded-lg text-sm text-gray-700 mb-6">
              <p>
                Enter your business details to generate a tailored profile. NAICS codes are optional but recommended.
                Need help? Check out this{" "}
                <a
                  href="https://samsearch.co/blog/naics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  NAICS guide
                </a>
                .
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              name="website"
              placeholder="e.g., www.example.com"
              className="bg-transparent placeholder-gray-500"
              {...register("website")}
            />
          </div>
          <Button
            type="button"
            onClick={handleGenerateProfile}
            disabled={isGenerating}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            {isGenerating ? "Generating..." : "Generate NAICS Codes & Company Profile"}
          </Button>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="naics_codes">NAICS Codes</Label>
              <NaicsCodeSelector
                value={naicsCodes}
                onChange={setNaicsCodes}
                disabled={isUpdatingProfile || isGenerating}
                placeholder="Search and select NAICS codes..."
              />
              {errors.naics_codes && <p className="text-red-500 text-sm mt-1">{errors.naics_codes.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="profile_description">Company Profile</Label>
              <textarea
                id="profile_description"
                name="profile_description"
                placeholder="Provide a brief description of your company and services."
                className="h-40 w-full bg-transparent border border-gray-300 rounded-md p-2 placeholder-gray-500 focus:ring focus:ring-indigo-500 focus:outline-none"
                disabled={isUpdatingProfile}
                {...register("profile_description")}
              />
              {errors.profile_description && (
                <p className="text-red-500 text-sm mt-1">{errors.profile_description.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <Button type="button" onClick={onStepPrevious} className="flex items-center">
              <FaLongArrowAltLeft className="mr-2" />
              Previous
            </Button>
            <Button type="submit" disabled={pending || isUpdatingProfile} className="flex items-center">
              {pending || isUpdatingProfile ? "Saving Profile..." : "Save and Continue"}
              {!pending && !isUpdatingProfile && <FaLongArrowAltRight className="ml-2" />}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ProfileForm

